import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormSelect,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSmartTable,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import LeagueService from 'src/service/LeagueService'
import Notify from '../Notify'
import LeagueForm from './LeagueForm'
import { LEAGUE_TYPE, deleteLeagueMessage } from './leagueHelpers'

const rankingColumns = [
  { key: 'rank', label: 'Rank', _style: { width: '7%' } },
  { key: 'manager_name', label: 'Manager' },
  { key: 'team_name', label: 'Fantasy Team' },
  { key: 'total_points', label: 'Season Points' },
  { key: 'round_points', label: 'Round Points' },
  { key: 'joined_at', label: 'Joined' },
  { key: 'actions', label: '', _style: { width: '1%' } },
]

const blockedColumns = [
  { key: 'manager_name', label: 'Manager' },
  { key: 'blocked_at', label: 'Removed' },
  { key: 'actions', label: '', _style: { width: '1%' } },
]

const formatDate = (value) => (value ? moment(value).format('DD MMM YYYY') : '-')

const LeagueDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [league, setLeague] = useState(null)
  const [rounds, setRounds] = useState([])
  const [notFound, setNotFound] = useState(false)
  const [roundId, setRoundId] = useState('')
  const [tab, setTab] = useState('rankings')
  const [rankings, setRankings] = useState([])
  const [blocked, setBlocked] = useState([])
  const [tableLoading, setTableLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [editVisible, setEditVisible] = useState(false)
  // { text, onConfirm } for the shared confirmation dialog.
  const [confirm, setConfirm] = useState(null)

  const fetchDetail = useCallback(() => {
    LeagueService.getLeagueDetail(id).then((res) => {
      if (res.success) {
        setLeague(res.data)
        setRounds(res.rounds || [])
        setRoundId((current) => current || res.rounds?.[res.rounds.length - 1]?.id || '')
      } else {
        setNotFound(true)
      }
    })
  }, [id])

  const fetchRankings = useCallback(() => {
    setTableLoading(true)
    LeagueService.getLeagueRankings(id, roundId)
      .then((res) => res.success && setRankings(res.data || []))
      .finally(() => setTableLoading(false))
  }, [id, roundId])

  const fetchBlocked = useCallback(() => {
    setTableLoading(true)
    LeagueService.getLeagueBlockedMembers(id)
      .then((res) => res.success && setBlocked(res.data || []))
      .finally(() => setTableLoading(false))
  }, [id])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  useEffect(() => {
    if (tab === 'rankings') fetchRankings()
    else fetchBlocked()
  }, [tab, fetchRankings, fetchBlocked])

  const runAction = (request, onDone) => {
    setActionLoading(true)
    request
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          onDone?.(res)
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Something went wrong. Please try again.', 'error'))
      .finally(() => setActionLoading(false))
  }

  const ask = (text, onConfirm) => setConfirm({ text, onConfirm })
  const handleConfirm = () => {
    const action = confirm?.onConfirm
    setConfirm(null)
    action?.()
  }

  const toggleJoining = () =>
    runAction(LeagueService.changeJoiningStatus({ id, is_open: !league.is_open }), fetchDetail)

  const resetCode = () =>
    ask(
      'Reset invite code? The old code will stop working. Existing members remain in the league.',
      () => runAction(LeagueService.resetLeagueCode({ id }), fetchDetail),
    )

  const copyCode = () =>
    navigator.clipboard
      .writeText(league.join_code || '')
      .then(() => ToastComponent('Code copied.', 'success'))
      .catch(() => ToastComponent('Could not copy the code.', 'error'))

  const deleteLeague = () =>
    ask(deleteLeagueMessage(league.name, league.members_count), () =>
      runAction(LeagueService.deleteLeague({ id }), () => navigate('/leagues')),
    )

  const removeMember = (member) =>
    ask(
      `Remove ${member.manager_name}? They will be blocked from rejoining until you allow access again. Their game team and points stay unchanged.`,
      () =>
        runAction(LeagueService.removeMember({ league_id: id, user_id: member.user_id }), () => {
          fetchRankings()
          fetchDetail()
        }),
    )

  const allowRejoin = (member) =>
    ask(
      `Allow ${member.manager_name} to rejoin? They can join again when joining is open. This does not add them automatically.`,
      () =>
        runAction(LeagueService.allowRejoin({ league_id: id, user_id: member.user_id }), () => {
          fetchBlocked()
          fetchDetail()
        }),
    )

  if (notFound) {
    return (
      <CCard>
        <CCardBody className="text-center py-5">
          <h4>League unavailable</h4>
          <p className="text-medium-emphasis">
            This league may have been deleted or belongs to another game.
          </p>
          <CButton color="primary" onClick={() => navigate('/leagues')}>
            Back to Leagues
          </CButton>
        </CCardBody>
      </CCard>
    )
  }

  if (!league) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  const isPrivate = Number(league.type) === LEAGUE_TYPE.PRIVATE

  return (
    <>
      <CButton color="link" className="px-0 mb-2" onClick={() => navigate('/leagues')}>
        <CIcon icon={cilArrowLeft} className="me-1" />
        Leagues
      </CButton>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title d-flex align-items-center gap-2">
            {league.name}
            <CBadge color={isPrivate ? 'warning' : 'info'} shape="rounded-pill" className="fs-6">
              {isPrivate ? 'Private' : 'Public'}
            </CBadge>
            <CBadge
              color={league.is_open ? 'success' : 'secondary'}
              shape="rounded-pill"
              className="fs-6"
            >
              {league.is_open ? 'Open' : 'Closed'}
            </CBadge>
          </h1>
          {league.description && <p className="ge-page-subtitle">{league.description}</p>}
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setEditVisible(true)}>
            Edit
          </CButton>
          <CButton
            color="secondary"
            variant="outline"
            disabled={actionLoading}
            onClick={toggleJoining}
          >
            {league.is_open ? 'Close Joining' : 'Open Joining'}
          </CButton>
          <CButton color="danger" variant="outline" disabled={actionLoading} onClick={deleteLeague}>
            Delete
          </CButton>
        </div>
      </div>

      <CRow className="g-3 mb-3">
        <CCol md={isPrivate ? 8 : 12}>
          <CCard className="h-100">
            <CCardBody>
              <CRow className="g-3">
                <CCol sm={4}>
                  <div className="small text-medium-emphasis">Owner</div>
                  <div className="fw-semibold">{league.owner_name || 'Game Admin'}</div>
                </CCol>
                <CCol sm={4}>
                  <div className="small text-medium-emphasis">Active Members</div>
                  <div className="fw-semibold">{league.members_count}</div>
                </CCol>
                <CCol sm={4}>
                  <div className="small text-medium-emphasis">Created</div>
                  <div className="fw-semibold">{formatDate(league.created_at)}</div>
                </CCol>
                {league.teams?.length > 0 && (
                  <CCol xs={12}>
                    <div className="small text-medium-emphasis">Teams</div>
                    <div className="fw-semibold">
                      {league.teams.map((team) => team.name).join(', ')}
                    </div>
                  </CCol>
                )}
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
        {isPrivate && (
          <CCol md={4}>
            <CCard className="h-100">
              <CCardBody>
                <div className="small text-medium-emphasis">Invite Code</div>
                <div className="fs-3 fw-bold font-monospace mb-2">{league.join_code || '-'}</div>
                <div className="d-flex gap-2">
                  <CButton size="sm" color="secondary" variant="outline" onClick={copyCode}>
                    Copy
                  </CButton>
                  <CButton
                    size="sm"
                    color="danger"
                    variant="outline"
                    disabled={actionLoading}
                    onClick={resetCode}
                  >
                    Reset Code
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        )}
      </CRow>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CNav variant="tabs">
              <CNavItem>
                <CNavLink
                  role="button"
                  active={tab === 'rankings'}
                  onClick={() => setTab('rankings')}
                >
                  Rankings ({league.members_count})
                </CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink
                  role="button"
                  active={tab === 'blocked'}
                  onClick={() => setTab('blocked')}
                >
                  Blocked ({league.blocked_count})
                </CNavLink>
              </CNavItem>
            </CNav>
            {tab === 'rankings' && rounds.length > 0 && (
              <CFormSelect
                style={{ maxWidth: 200 }}
                value={roundId}
                aria-label="Round for round points"
                onChange={(e) => setRoundId(e.target.value)}
              >
                {rounds.map((round) => (
                  <option key={round.id} value={round.id}>
                    Round {round.round}
                  </option>
                ))}
              </CFormSelect>
            )}
          </div>

          <div className="ge-datatable ge-datatable--no-select">
            {tab === 'rankings' ? (
              <CSmartTable
                columns={rankingColumns}
                items={rankings}
                loading={tableLoading || actionLoading}
                noItemsLabel="No active members with a saved team yet."
                pagination
                itemsPerPage={25}
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                scopedColumns={{
                  manager_name: (item) => (
                    <td>
                      {item.manager_name}
                      {item.is_owner && (
                        <CBadge color="danger" shape="rounded-pill" className="ms-2">
                          Owner
                        </CBadge>
                      )}
                    </td>
                  ),
                  team_name: (item) => <td>{item.team_name || '-'}</td>,
                  joined_at: (item) => <td>{formatDate(item.joined_at)}</td>,
                  actions: (item) => (
                    <td>
                      {!item.is_owner && (
                        <CButton
                          size="sm"
                          color="danger"
                          variant="outline"
                          disabled={actionLoading}
                          onClick={() => removeMember(item)}
                        >
                          Remove
                        </CButton>
                      )}
                    </td>
                  ),
                }}
              />
            ) : (
              <CSmartTable
                columns={blockedColumns}
                items={blocked}
                loading={tableLoading || actionLoading}
                noItemsLabel="No blocked members."
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                scopedColumns={{
                  blocked_at: (item) => <td>{formatDate(item.blocked_at)}</td>,
                  actions: (item) => (
                    <td>
                      <CButton
                        size="sm"
                        color="primary"
                        variant="outline"
                        className="text-nowrap"
                        disabled={actionLoading}
                        onClick={() => allowRejoin(item)}
                      >
                        Allow Rejoining
                      </CButton>
                    </td>
                  ),
                }}
              />
            )}
          </div>
        </CCardBody>
      </CCard>

      <LeagueForm
        visible={editVisible}
        league={league}
        onClose={() => setEditVisible(false)}
        onSaved={fetchDetail}
      />

      <Notify
        showConfirm={!!confirm}
        handleCancel={() => setConfirm(null)}
        handleConfirm={handleConfirm}
        text={confirm?.text || ''}
      />
    </>
  )
}

export default LeagueDetail
